import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import KanbanBoard from '../../components/admin/KanbanBoard';

interface Request {
  id: string;
  type: string;
  status: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  assigned_to: string | null;
  brand_id: string;
  answers: any;
  questions_and_answers: any;
  assigned_user?: {
    user_metadata: {
      full_name?: string;
    };
    email?: string;
  };
  brands?: {
    name: string;
    description?: string;
  };
  unreadCount?: number;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('kanban');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    review: 0,
    completed: 0,
    averageTurnaround: 0
  });
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      
      try {
        // First, fetch all service requests with brand information
        const { data: requestsData, error: requestsError } = await supabase
          .from('service_requests')
          .select(`
            *,
            brands (
              id,
              name,
              description
            )
          `)
          .order('created_at', { ascending: false });

        if (requestsError) {
          console.error('Error fetching requests:', requestsError);
          setLoading(false);
          return;
        }

        if (requestsData) {
          // Get unique assigned user IDs (filter out nulls)
          const assignedUserIds = Array.from(new Set(
            requestsData
              .filter(req => req.assigned_to)
              .map(req => req.assigned_to)
          ));

          let requestsWithUsers = requestsData;

          // If there are assigned users, fetch their details
          if (assignedUserIds.length > 0) {
            
            const { data: usersData, error: usersError } = await supabase
              .from('users')
              .select('id, email, full_name')
              .in('id', assignedUserIds);

            if (usersError) {
              console.error('Error fetching users:', usersError);
            } else if (usersData) {
              
              // Create a map of user data for quick lookup
              const usersMap = new Map(
                usersData.map(user => [user.id, user])
              );

              // Add user information to requests
              requestsWithUsers = requestsData.map(request => {
                if (request.assigned_to) {
                  const userData = usersMap.get(request.assigned_to);
                  
                  return {
                    ...request,
                    assigned_user: {
                      user_metadata: {
                        full_name: userData?.full_name
                      },
                      email: userData?.email
                    }
                  };
                }
                return request;
              });
            }
          }

          // Fetch unread message counts for each request
          const requestIds = requestsWithUsers.map(req => req.id);
          const { data: messageCounts, error: messageError } = await supabase
            .from('request_message')
            .select('request_id, sender, read')
            .in('request_id', requestIds)
            .eq('sender', 'user') // Only count messages from users (clients)
            .eq('read', false); // Only count unread messages

          if (!messageError && messageCounts) {
            // Count messages per request
            const unreadCounts = messageCounts.reduce((acc: any, msg) => {
              acc[msg.request_id] = (acc[msg.request_id] || 0) + 1;
              return acc;
            }, {});

            // Add unread counts to requests
            requestsWithUsers = requestsWithUsers.map(req => ({
              ...req,
              unreadCount: unreadCounts[req.id] || 0
            }));
          }

          setRequests(requestsWithUsers);
          calculateStats(requestsWithUsers);
        }
      } catch (err) {
        console.error('Exception fetching requests:', err);
      }
      
      setLoading(false);
    };

    fetchRequests();
  }, []);

  // Set up real-time subscription for new messages
  useEffect(() => {
    // Subscribe to new messages to update notification counts
    const messageSubscription = supabase
      .channel('dashboard_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'request_message',
        },
        async (payload) => {
          const newMessage = payload.new;
          
          // Only update if it's a message from a user (client) that's unread
          if (newMessage.sender === 'user' && !newMessage.read) {
            console.log('🔔 New unread message received in dashboard:', newMessage);
            
            // Update the unread count for this request
            setRequests(prev => prev.map(req => 
              req.id === newMessage.request_id 
                ? { ...req, unreadCount: (req.unreadCount || 0) + 1 }
                : req
            ));
          }
        }
      )
      .subscribe();

    return () => {
      messageSubscription.unsubscribe();
    };
  }, []);

  const calculateStats = (requests: Request[]) => {
    const now = new Date();
    const stats = {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      inProgress: requests.filter(r => r.status === 'in_progress').length,
      review: requests.filter(r => r.status === 'review').length,
      completed: requests.filter(r => r.status === 'completed').length,
      averageTurnaround: 0
    };

    // Calculate average turnaround time for completed requests
    const completedRequests = requests.filter(r => r.status === 'completed');
    if (completedRequests.length > 0) {
      const totalTurnaround = completedRequests.reduce((acc, req) => {
        const created = new Date(req.created_at);
        const completed = new Date(req.updated_at || req.created_at);
        return acc + (completed.getTime() - created.getTime());
      }, 0);
      stats.averageTurnaround = totalTurnaround / completedRequests.length;
    }

    setStats(stats);
  };

  const handleAssignRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ 
          assigned_to: user?.id || null,
          status: 'in_progress'
        })
        .eq('id', requestId);

      if (!error) {
        // Fetch current user information
        let assignedUser: Request['assigned_user'] = undefined;
        if (user?.id) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, email, full_name')
            .eq('id', user.id)
            .single();
          
          if (userError) {
            console.error('Error fetching current user:', userError);
          } else if (userData) {
            assignedUser = {
              user_metadata: {
                full_name: userData.full_name
              },
              email: userData.email
            };
          }
        }
        
        // Update the local state with user information
        const updatedRequests = requests.map(req => 
          req.id === requestId 
            ? { 
                ...req, 
                assigned_to: user?.id || null, 
                status: 'in_progress',
                assigned_user: assignedUser
              }
            : req
        );
        setRequests(updatedRequests);
        
        // Recalculate stats with updated data
        calculateStats(updatedRequests);
        
        // Show success message
        setToast({ type: 'success', message: 'Request assigned successfully!' });
      } else {
        console.error('Error assigning request:', error);
        setToast({ type: 'error', message: `Failed to assign request: ${error.message}` });
      }
    } catch (err) {
      console.error('Exception assigning request:', err);
      setToast({ type: 'error', message: 'An unexpected error occurred while assigning the request.' });
    }
    
    // Clear toast after 3 seconds
    setTimeout(() => setToast(null), 3000);
  };

  const handleStatusChange = async (requestId: string, newStatus: string) => {
    const { error } = await supabase
      .from('service_requests')
      .update({ 
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId);

    if (!error) {
      const updatedRequests = requests.map(req => 
        req.id === requestId 
          ? { ...req, status: newStatus }
          : req
      );
      setRequests(updatedRequests);
      calculateStats(updatedRequests);
      setToast({ type: 'success', message: 'Status updated successfully!' });
    } else {
      setToast({ type: 'error', message: 'Failed to update status.' });
    }
    setTimeout(() => setToast(null), 2500);
  };

  return (
    <div className="p-8">
      {toast && (
        <div className={`mb-4 px-4 py-2 rounded text-white font-medium ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Admin Dashboard</h1>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm">Total Requests</h3>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm">Pending</h3>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm">In Progress</h3>
          <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm">Review</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.review}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm">Completed</h3>
          <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {viewMode === 'kanban' ? 'Kanban Board' : 'Requests Table'}
        </h2>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('kanban')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'kanban'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📋 Kanban
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 Table
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading requests...</p>
        </div>
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          requests={requests}
          onStatusChange={handleStatusChange}
          onAssignRequest={handleAssignRequest}
        />
      ) : (
        /* Requests Table */
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notifications</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.unreadCount && request.unreadCount > 0 ? (
                          <div className="relative">
                            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {request.unreadCount > 9 ? '9+' : request.unreadCount}
                              </span>
                            </div>
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.brands?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {request.type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={request.status}
                          onChange={(e) => handleStatusChange(request.id, e.target.value)}
                          className="text-sm border rounded px-2 py-1"
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="review">Review</option>
                          <option value="completed">Completed</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {request.assigned_to ? (
                          <div className="flex items-center space-x-1">
                            <span className="text-green-600">👤</span>
                            <span className="text-green-600 font-medium">
                              {request.assigned_user?.user_metadata?.full_name || 
                               request.assigned_user?.email?.split('@')[0] || 
                               'Unknown User'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500">📭 Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {!request.assigned_to && (
                          <button
                            onClick={() => handleAssignRequest(request.id)}
                            className="text-primary hover:text-primary/80 mr-4"
                          >
                            Assign to me
                          </button>
                        )}
                        <Link
                          to={`/admin/requests/${request.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 